import type React from "react";
import {
  Box,
  Paper,
  Grid,
  CircularProgress,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { Share as ShareIcon, Print as PrintIcon } from "@mui/icons-material";
import { motion, AnimatePresence, type Variants } from "framer-motion";
import { useReactToPrint } from "react-to-print";
import AddCrewModal from "./AddCrewModal";
import type {
  IExpandedCallSheetData,
  ICallSheetPageProps,
  IUser,
} from "../../../../types/callsheet";
import { tokens } from "../../../../theme";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../store/Index";
import { useNavigate, useParams } from "react-router-dom";
import apiService from "../../../../service/Api";
import { toast } from "react-toastify";
import type {
  CallSheetData,
  CallSheetLayoutProps,
  CrewEntry,
  CrewEntryResponse,
} from "./types/callsheet";
import type { IEquipment } from "../../../../types/equipment";
import CallSheetHeader from "./CallSheetHeader";
import CallSheetMainInfo from "./CallSheetMainInfo";
import CallSheetSchedule from "./CallSheetSchedule";
import CrewTable from "./CrewTable";
import ClientDetails from "./ClientDetails";
import PrintableCallSheet from "./PrintableCallsheet";
import EventDetailsPage from "./CallSheetEventDetails";
import PrintableEventDetails from "./PrintableEventDetails";

import { useEffect, useState, useRef, useCallback } from "react";
import type { TimeAllocation } from "../../hooks/useStreamData";

const availableRoles = [
  "Executive Producer",
  "Producer",
  "Assistant Producer",
  "Photographer",
  "Client Interface",
  "Liveswitcher",
  "Networking",
  "Sound",
  "Graphics",
  "Graphics Designer",
  "Tribute Table",
  "Camera One",
  "Camera Two",
  "Camera Three",
  "Camera Four",
  "Camera Five",
  "Camera Assistant",
];

const CallSheetPage: React.FC<ICallSheetPageProps> = () => {
  const navigate = useNavigate();
  const [callSheetData, setCallSheetData] =
    useState<IExpandedCallSheetData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<IUser[]>([]);
  const [addCrewModalOpen, setAddCrewModalOpen] = useState(false);
  const [showAddButton, setShowAddButton] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const { id } = useParams();
  const [crewMembers, setCrewMembers] = useState<CrewEntry[]>([]);
  const [allocatedEquipment, setAllocatedEquipment] = useState<IEquipment[]>(
    []
  );
  const [addEquipmentModalOpen, setAddEquipmentModalOpen] = useState(false);
  const [equipmentList, setEquipmentList] = useState<IEquipment[]>([]);
  const [equipmentLoading, setEquipmentLoading] = useState(false);
  const [equipmentError, setEquipmentError] = useState<string | null>(null);
  const [callSheetError, setCallSheetError] = useState(false);
  const [crewError, setCrewError] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredUsers, setFilteredUsers] = useState<IUser[]>([]);
  const [timeData, setTimeData] = useState<CallSheetData | null>({
    call_time: "N/A",
    departure_time: "N/A",
    arrival_at_venue_time: "N/A",
    setup_from_time: "N/A",
    setup_to_time: "N/A",
    shoot_start_time: "N/A",
    shoot_end_time: "N/A",
    estimated_wrap_time: "N/A",
    back_to_base_time: "N/A",
    unpacking_time: "N/A",
    depart_time: "N/A",
  });
  const [eventDetails, setEventDetails] = useState({
    priest_name: "",
    eulogist_name: "",
    mc_name: "",
    other: "",
    program: "",
    notes: {
      travel: "",
      catering: "",
      logistics: "",
      technical: "",
      special_requirements: "",
      additional_notes: "",
    },
  });

  // Print ref
  const printRef = useRef<HTMLDivElement>(null);

  const printEventDetailsRef = useRef<HTMLDivElement>(null);

  const handlePrintClick = useCallback(() => {
    if (!callSheetData) {
      toast.error("No data to print");
      return;
    }

    try {
      // Create a combined print window with both pages
      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Call Sheet - ${
                callSheetData?.booking?.deceased[0]?.first_name || "Unknown"
              } ${callSheetData?.booking?.deceased[0]?.surname || ""}</title>
              <style>
                @page {
                  size: A4 portrait;
                  margin: 0.5in;
                }
                body {
                  font-family: Arial, sans-serif;
                  margin: 0;
                  padding: 0;
                  -webkit-print-color-adjust: exact;
                  color-adjust: exact;
                  print-color-adjust: exact;
                }
                .page-break {
                  page-break-before: always;
                }
              </style>
            </head>
            <body>
              ${printRef.current?.innerHTML || ""}
              <div class="page-break">
                ${printEventDetailsRef.current?.innerHTML || ""}
              </div>
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }
    } catch (error) {
      console.error("Print failed:", error);
      toast.error("Print failed. Please try again.");
    }
  }, [callSheetData]);

  // Animation variants (keeping your original animations)
  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants: Variants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut" as const,
      },
    },
  };

  // All your existing useEffect and handler functions remain exactly the same
  useEffect(() => {
    const fetchEquipment = async () => {
      setEquipmentLoading(true);
      setEquipmentError(null);
      try {
        const response = await apiService.get<{ results: any[] }>(
          "/api/equipments/equipment/"
        );
        const equipmentData: IEquipment[] = response.data.results.map(
          (item) => ({
            id: item.id.toString(),
            serialNo: item.serial_number || item.serialNo || "",
            name: item.name || "",
            brand: item.brand || "",
            category: item.category || "",
            subCategory: item.sub_category || item.subCategory || "",
            purchaseDate: item.purchase_date || item.purchaseDate || "",
            status: item.status || "",
            lastServiceDate:
              item.last_service_date || item.lastServiceDate || undefined,
            hoursSinceLastService:
              item.hours_since_last_service ||
              item.hoursSinceLastService ||
              undefined,
            assignee: item.assignee || undefined,
            nextMaintenance:
              item.next_maintenance || item.nextMaintenance || undefined,
          })
        );
        setEquipmentList(equipmentData);
      } catch (err: any) {
        setEquipmentError("Failed to load equipment. Please try again.");
      } finally {
        setEquipmentLoading(false);
      }
    };
    fetchEquipment();
  }, []);

  useEffect(() => {
    const fetchCallSheet = async () => {
      try {
        const response = await apiService.get<IExpandedCallSheetData>(
          `/api/streams/stream/${id}/`
        );

        toast.success("Callsheet data fetched successfully.");
        setCallSheetData(response.data);
        fetchCrewMembers();
      } catch (error) {
        console.error("Failed to fetch call sheet", error);
        toast.error(`Failed to fetch call sheet: ${error}`);
        setCallSheetError(true);
        setCallSheetData(null);
      }
    };

    if (id) {
      fetchCallSheet();
      fetchCrewMembers();
    }
  }, [id]);

  const fetchCrewMembers = async () => {
    try {
      const res = await apiService.get<CrewEntryResponse>(
        `/api/call-sheets/callsheet/?stream=${id}`
      );

      const timeResponse = await apiService.get<TimeAllocation>(
        `/api/streams/stream/${id}/time-algorithim/`
      );
      const timeJson = timeResponse.data;
      setTimeData({
        call_time: timeJson.call_time || "N/A",
        departure_time: timeJson.departure_time || "N/A",
        arrival_at_venue_time: timeJson.arrival_at_venue_time || "N/A",
        setup_from_time: timeJson.setup_from_time || "N/A",
        setup_to_time: timeJson.setup_to_time || "N/A",
        shoot_start_time: timeJson.shoot_start_time || "N/A",
        shoot_end_time: timeJson.shoot_end_time || "N/A",
        estimated_wrap_time: timeJson.estimated_wrap_time || "N/A",
        back_to_base_time: timeJson.back_to_base_time || "N/A",
        unpacking_time: timeJson.unpacking_time || "N/A",
        depart_time: timeJson.depart_time || "N/A",
      });

      const crewData = res.data.results as CrewEntry[];
      setCrewMembers(crewData);
    } catch (error) {
      console.error("Error fetching crew members:", error);
      setCrewError(true);
      setCrewMembers([]);
    }
  };

  const fetchUsers = async (search: string = "") => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.get<{
        count: number;
        next: string | null;
        previous: string | null;
        results: IUser[];
      }>(`/api/crews/crew-members/?search=${search}`);
      setUsers(response.data.results);
      setFilteredUsers(response.data.results);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
        toast.error(`${err.message}`);
      } else {
        setError("An unknown error occurred.");
        toast.error("An unknown error occurred.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Debounce searchTerm changes
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchUsers(searchTerm);
    }, 400);

    return () => clearTimeout(timeout);
  }, [searchTerm]);

  // Run when currentPage changes
  useEffect(() => {
    if (currentPage === 1) fetchUsers();
    // if (currentPage === 2) fetchEventNotes();
  }, [currentPage]);

  const handleAddCrew = async (user: IUser, role: string) => {
    const stream_id = Number(id);
    const crew_member_id = Number.parseInt(user.id);
    try {
      const payload = {
        stream_id: stream_id,
        crew_member_id: crew_member_id,
        assignment: [{ role: role, notes: "" }],
      };
      const response = await apiService.post(
        "/api/call-sheets/callsheet/",
        payload
      );
      toast.success(`${user.user.first_name} added as ${role} successfully!`);
    } catch (error: any) {
      console.error(`Error adding ${user.user.first_name} as ${role}:`, error);
      let errorMessage = `Failed to add ${user.user.first_name} as ${role}.`;
      if (error.response && error.response.data) {
        if (typeof error.response.data === "string") {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.non_field_errors) {
          errorMessage = error.response.data.non_field_errors.join(", ");
        } else if (Object.keys(error.response.data).length > 0) {
          errorMessage = Object.entries(error.response.data)
            .map(
              ([key, value]) =>
                `${key}: ${Array.isArray(value) ? value.join(", ") : value}`
            )
            .join("; ");
        }
      }
      toast.error(errorMessage);
      throw error;
    }
  };

  const handleDeleteCrew = async (entryId: number) => {
    try {
      await apiService.delete(`/api/call-sheets/callsheet/${entryId}/`);
      toast.success("Crew member removed successfully!");
      await fetchCrewMembers();
    } catch (error: any) {
      console.error("Error removing crew member:", error);
      let errorMessage = "Failed to remove crew member.";
      if (error.response && error.response.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      toast.error(errorMessage);
    }
  };

  const handleCreateUser = async (
    userData: Omit<IUser, "id">
  ): Promise<IUser> => {
    try {
      const response = await apiService.post<IUser>(
        "/api/crews/crew-members/full-create/",
        userData
      );
      const newUser = response.data;
      setUsers((prev) => {
        return [...prev, newUser];
      });
      toast.success(`User '${newUser.user.first_name}' created successfully!`);
      return newUser;
    } catch (err: any) {
      let errorMessage = "Failed to create user. Please try again.";
      if (err.response?.status === 401) {
        errorMessage = "Unauthorized: Please log in again.";
        toast.error(errorMessage);
        navigate("/login");
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
        toast.error(`Error: ${errorMessage}`);
      } else if (err.response?.data) {
        const serverErrors = Object.values(err.response.data).flat().join(" ");
        errorMessage = serverErrors || errorMessage;
        toast.error(`Error: ${errorMessage}`);
      }
      throw new Error(errorMessage);
    }
  };

  const handleShare = async () => {
    if (navigator.share && callSheetData?.website_stream_link) {
      try {
        await navigator.share({
          title: `${callSheetData?.booking?.deceased[0].first_name} ${callSheetData?.booking?.deceased[0].surname} - Memorial Service`,
          url: callSheetData.website_stream_link,
        });
      } catch (error) {}
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{
            duration: 1,
            repeat: Number.POSITIVE_INFINITY,
            ease: "linear",
          }}
        >
          <CircularProgress sx={{ color: reduxTheme.primary_colors[400] }} />
        </motion.div>
      </Box>
    );
  }

  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="error-container"
      >
        <Typography color="error">Error: {error}</Typography>
      </motion.div>
    );
  }

  if (!callSheetData) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <Typography>Booking not found.</Typography>
      </motion.div>
    );
  }

  return (
    <>
      {/* Hidden Printable Components */}
      <div style={{ position: "absolute", left: "-9999px", top: "-9999px" }}>
        <PrintableCallSheet
          ref={printRef}
          callSheetData={callSheetData}
          crewMembers={crewMembers}
          timeData={timeData}
          reduxTheme={reduxTheme}
          specialRequest={
            callSheetData?.booking?.services[0]?.special_request || "N/A"
          }
        />
        <PrintableEventDetails
          ref={printEventDetailsRef}
          callSheetData={callSheetData}
          eventDetails={eventDetails}
          reduxTheme={reduxTheme}
        />
      </div>

      {/* Main Display Component */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{
          color: colors.primary[400],
          width: "210mm",
          margin: "0 auto",
          minHeight: "297mm",
        }}
      >
        {/* Header Actions */}
        <motion.div variants={itemVariants}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              mt: 2,
              mb: 5,
            }}
          >
            <Box sx={{ display: "flex", gap: 1 }}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setCurrentPage(1)}
                style={{
                  padding: "8px 16px",
                  backgroundColor:
                    currentPage === 1
                      ? reduxTheme.primary_colors[400]
                      : reduxTheme.primary_colors[800],
                  color:
                    currentPage === 1
                      ? colors.primary[900]
                      : colors.primary[100],
                  border: `1px solid ${reduxTheme.primary_colors[800]}`,
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "12px",
                  fontWeight: "bold",
                }}
              >
                Page 1-Crew Assignment
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setCurrentPage(2)}
                style={{
                  padding: "8px 16px",
                  backgroundColor:
                    currentPage === 2
                      ? reduxTheme.primary_colors[400]
                      : reduxTheme.primary_colors[800],
                  color:
                    currentPage === 2
                      ? colors.primary[900]
                      : colors.primary[100],
                  border: `1px solid ${reduxTheme.primary_colors[800]}`,
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "12px",
                  fontWeight: "bold",
                }}
              >
                Page 2 - Event Notes
              </motion.button>
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                <IconButton
                  onClick={handlePrintClick}
                  sx={{
                    color: colors.primary[100],
                    backgroundColor: reduxTheme.primary_colors[800],
                  }}
                >
                  <PrintIcon />
                </IconButton>
              </motion.div>
              <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                <IconButton
                  onClick={handleShare}
                  disabled
                  sx={{
                    backgroundColor: reduxTheme.primary_colors[800],
                    color: colors.primary[100],
                    mr: 2,
                  }}
                >
                  <ShareIcon />
                </IconButton>
              </motion.div>
            </Box>
          </Box>
        </motion.div>

        {/* Page Content */}
        <AnimatePresence mode="wait">
          {currentPage === 1 && (
            <motion.div
              key="page1"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Main Content */}
              <motion.div variants={itemVariants}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 1,
                    overflow: "hidden",
                    maxWidth: 1200,
                  }}
                >
                  <Grid container spacing={2}>
                    {/* Left Section */}
                    <Grid
                      size={{ xs: 12, md: 4 }}
                      sx={{
                        borderRight: {
                          md: `1px solid ${reduxTheme.grey_colors[500]}`,
                        },
                      }}
                    >
                      <CallSheetHeader
                        callSheetData={callSheetData ?? {}}
                        crewMembers={crewMembers ?? []}
                      />
                    </Grid>

                    {/* Middle Section */}
                    <Grid
                      size={{ xs: 12, md: 4 }}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        borderRight: {
                          md: `1px solid ${reduxTheme.grey_colors[500]}`,
                        },
                      }}
                    >
                      <CallSheetMainInfo
                        callSheetData={callSheetData}
                        reduxTheme={reduxTheme}
                      />
                    </Grid>

                    {/* Right Section */}
                    <Grid size={{ xs: 12, md: 4 }}>
                      <CallSheetSchedule
                        callSheetData={callSheetData ?? {}}
                        timeData={timeData}
                        reduxTheme={reduxTheme}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </motion.div>

              {/* Crew Table */}
              <motion.div variants={itemVariants}>
                <CrewTable
                  crewMembers={crewMembers ?? []}
                  colors={colors}
                  reduxTheme={reduxTheme}
                  showAddButton={showAddButton}
                  onAddCrewClick={() => setAddCrewModalOpen(true)}
                  onMouseEnter={() => setShowAddButton(true)}
                  onMouseLeave={() => setShowAddButton(false)}
                  onDeleteCrew={handleDeleteCrew}
                />
              </motion.div>

              {/* Client and Location Details */}
              <ClientDetails
                callSheetData={callSheetData}
                reduxTheme={reduxTheme}
                specialRequest={
                  callSheetData?.booking?.services[0].special_request || "N/A"
                }
              />
            </motion.div>
          )}

          {currentPage === 2 && (
            <motion.div
              key="page2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <EventDetailsPage
                callSheetData={callSheetData}
                reduxTheme={reduxTheme}
                colors={colors}
                eventDetails={eventDetails}
                setEventDetails={setEventDetails}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* <CallSheetPagination
          currentPage={currentPage}
          totalPages={totalPages}
          reduxTheme={reduxTheme}
          onPageChange={(page) => setCurrentPage(page)}
        /> */}

        <AddCrewModal
          open={addCrewModalOpen}
          onClose={() => setAddCrewModalOpen(false)}
          onAddCrew={handleAddCrew}
          fetchCrewMembers={fetchCrewMembers}
          onCreateUser={handleCreateUser}
          existingUsers={users}
          availableRoles={availableRoles}
          theme={theme}
          colors={colors}
        />
      </motion.div>
    </>
  );
};

const CallSheetLayout: React.FC<CallSheetLayoutProps> = ({
  booking,
  booking_type,
  sx = {},
}) => {
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "900px",
        margin: "0 auto",
        backgroundColor: "background.paper",
        borderRadius: 2,
        boxShadow: 0,
        overflow: "hidden",
        ...sx,
      }}
    >
      <CallSheetPage booking={booking} booking_type={booking_type} />
    </Box>
  );
};

export default CallSheetLayout;
